import { Action, Actions, ofActionDispatched, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import {
  DownloadGeneratedReport,
  MonitoringReportDataLoaded,
  ResetReportGeneration,
  ResumeReportGeneration,
  StartReportGeneration,
} from '@shared/redux/report-generation.actions';
import { EMPTY, takeUntil, tap } from 'rxjs';
import { SnackbarV3Service } from '@gea/digital-ui-lib';
import { catchError, finalize } from 'rxjs/operators';
import { showSnackbarError } from '@shared/utils/shared-helpers';
import { NETWORK_ERROR_MESSAGE, NETWORK_ERROR_TITLE, REPORT_GENERATION_FAILED_MESSAGE } from '@shared/models/translations';
import { ReportStatus } from '@shared/models/common.models';
import { UserLocaleService } from '@shared/services/user-locale/user-locale.service';
import { ReportStreamService } from '@shared/services/report-stream/report-stream.service';
import { ReportDownloadService } from '@shared/services/report-download/report-download.service';
import { ReportGenerationStorageService } from '@shared/services/report-generation-storage/report-generation-storage.service';
import { MachineTriggerReportRequest } from '@generated-apis/model/machineTriggerReportRequest';
import {
  isResumeRejection,
  isTerminalStreamError,
  ReportStreamEvent,
  ReportStreamEventType,
} from '@shared/services/report-stream/report-stream.models';
import { formatInTimeZone } from 'date-fns-tz';

export interface ReportGenerationStateModel {
  reportId: string | null;
  startDate: string | null;
  stopDate: string | null;
  geaPUID: string | null;
  serialNumber: string | null;
  reportGenerationLoading: boolean;
  reportStatus: ReportStatus | null;
  timezone: string | null;
  manufacturer: string | null;
  machineDisplayName: string | null;
  yearOfManufacturing: string | null;
}

const defaults: ReportGenerationStateModel = {
  reportId: null,
  startDate: null,
  stopDate: null,
  geaPUID: null,
  serialNumber: null,
  reportGenerationLoading: false,
  reportStatus: null,
  timezone: null,
  manufacturer: null,
  machineDisplayName: null,
  yearOfManufacturing: null,
};

@State<ReportGenerationStateModel>({
  name: 'reportGeneration',
  defaults: defaults,
})
@Injectable()
export class ReportGenerationState {
  private readonly reportStreamService = inject(ReportStreamService);
  private readonly reportDownloadService = inject(ReportDownloadService);
  private readonly storageService = inject(ReportGenerationStorageService);
  private readonly snackbarService = inject(SnackbarV3Service);
  private readonly userLocaleService = inject(UserLocaleService);
  private readonly actions$ = inject(Actions);

  @Selector()
  static reportGenerationLoading(state: ReportGenerationStateModel) {
    return state.reportGenerationLoading;
  }

  @Selector()
  static reportStatus(state: ReportGenerationStateModel) {
    return state.reportStatus;
  }

  @Selector()
  static reportGeneralData(state: ReportGenerationStateModel): Partial<ReportGenerationStateModel> {
    return {
      timezone: state.timezone,
      serialNumber: state.serialNumber,
      manufacturer: state.manufacturer,
      machineDisplayName: state.machineDisplayName,
      yearOfManufacturing: state.yearOfManufacturing,
      reportId: state.reportId,
      startDate: state.startDate,
      stopDate: state.stopDate,
      reportStatus: state.reportStatus,
    };
  }

  @Action(StartReportGeneration, { cancelUncompleted: true })
  startReportGeneration(ctx: StateContext<ReportGenerationStateModel>, action: StartReportGeneration) {
    const dateRange = this.userLocaleService.convertDateRangeToUserTimeZone(action.dateRange);
    const request: MachineTriggerReportRequest = {
      start: dateRange.startDate,
      stop: dateRange.endDate,
      timezone: this.userLocaleService.userTimezone(),
    };

    this.storageService.save({ ...request, geaPUID: action.geaPUID, createdAt: Date.now() });

    ctx.patchState({
      reportGenerationLoading: true,
      reportStatus: 'PENDING',
      geaPUID: action.geaPUID,
      startDate: request.start,
      stopDate: request.stop,
      timezone: request.timezone,
    });

    return this.runStream(ctx, action.geaPUID, request);
  }

  @Action(ResumeReportGeneration)
  resumeReportGeneration(ctx: StateContext<ReportGenerationStateModel>, action: ResumeReportGeneration) {
    const { geaPUID, start, stop, timezone } = action.request;

    ctx.patchState({
      reportGenerationLoading: true,
      reportStatus: 'PENDING',
      geaPUID: geaPUID,
      startDate: start,
      stopDate: stop,
      timezone: timezone,
    });

    return this.runStream(ctx, geaPUID, { start, stop, timezone }, true);
  }

  @Action(DownloadGeneratedReport)
  downloadGeneratedReport(ctx: StateContext<ReportGenerationStateModel>, action: DownloadGeneratedReport) {
    const state = ctx.getState();
    const fileName = this.reportDownloadService.resolveFileName(
      {
        serialNumber: state.serialNumber,
        geaPUID: state.geaPUID,
        startDate: state.startDate,
        stopDate: state.stopDate,
        timezone: state.timezone,
      },
      action.downloadPath
    );

    return this.reportDownloadService.downloadReport(action.downloadPath).pipe(
      tap((blob) => {
        this.reportDownloadService.saveBlob(blob, fileName);
      }),
      catchError(() => {
        showSnackbarError(this.snackbarService, NETWORK_ERROR_TITLE, NETWORK_ERROR_MESSAGE);
        return EMPTY;
      }),
      finalize(() => {
        ctx.patchState({ reportGenerationLoading: false });
      })
    );
  }

  @Action(MonitoringReportDataLoaded)
  reportDataLoaded(ctx: StateContext<ReportGenerationStateModel>, action: MonitoringReportDataLoaded) {
    const start = action.report.start;
    const stop = action.report.stop;
    const timezone = action.report.timezone;

    const stateUpdate: Partial<ReportGenerationStateModel> = {
      reportId: action.report.report_id,
      serialNumber: action.report.serialnumber,
      manufacturer: action.report.manufacturer,
      machineDisplayName: action.report.machineDisplayName,
      yearOfManufacturing: action.report.yearOfManufacture,
      timezone: timezone,
    };

    if (start && stop && timezone) {
      stateUpdate.startDate = formatInTimeZone(start, timezone, 'yyyy-MM-dd');
      stateUpdate.stopDate = formatInTimeZone(stop, timezone, 'yyyy-MM-dd');
    }

    ctx.patchState(stateUpdate);
  }

  @Action(ResetReportGeneration)
  resetReportGeneration(ctx: StateContext<ReportGenerationStateModel>) {
    this.storageService.clear();
    ctx.patchState(defaults);
  }

  private runStream(
    ctx: StateContext<ReportGenerationStateModel>,
    geaPUID: string,
    request: MachineTriggerReportRequest,
    isResume = false
  ) {
    return this.reportStreamService.openStream(geaPUID, request).pipe(
      takeUntil(this.actions$.pipe(ofActionDispatched(ResetReportGeneration))),
      tap((event) => this.applyStreamEvent(ctx, event)),
      catchError((error: unknown) => this.handleStreamError(ctx, error, isResume))
    );
  }

  private applyStreamEvent(ctx: StateContext<ReportGenerationStateModel>, event: ReportStreamEvent): void {
    if (event.type === ReportStreamEventType.Status) {
      ctx.patchState({ reportStatus: event.status });
    }

    if (event.type === ReportStreamEventType.Complete) {
      this.storageService.clear();
      ctx.patchState({ reportStatus: 'COMPLETED' });
      ctx.dispatch(new DownloadGeneratedReport(event.downloadPath));
    }
  }

  private handleStreamError(ctx: StateContext<ReportGenerationStateModel>, error: unknown, isResume: boolean) {
    ctx.patchState(defaults);

    if (isResume && isResumeRejection(error)) {
      this.storageService.clear();
      return EMPTY;
    }

    if (isTerminalStreamError(error)) {
      this.storageService.clear();
    }

    showSnackbarError(this.snackbarService, NETWORK_ERROR_TITLE, REPORT_GENERATION_FAILED_MESSAGE);
    return EMPTY;
  }
}
